import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BuyerDashboardFilterPagination } from '../../types';

export enum BuyerDashboardGrouping {
  VOYAGE = 'VOYAGE',
  APPOINTMENT = 'APPOINTMENT',
  PORT_CALL = 'PORT_CALL'
}

export type BuyerDashboardState = {
  pagination: BuyerDashboardFilterPagination;
  filters: {
    vessel: string;
    port: string;
    appointmentId: string;
    agent: string;
    portCallId: string;
    regionId: string;
    assignee: string;
    voyageNumber: string;
    etaFromDate: string;
    etaToDate: string;
    etsFromDate: string;
    etsToDate: string;
    myPendingTasks: boolean;
    mySubmittedAppointments: boolean;
    opsCoordinator: string;
  };
  grouping: BuyerDashboardGrouping;
  /**
   * Bumped whenever filters are reset. The filter bar watches it to also clear its
   * local UI state (empty chips, input text), so a reset triggered from anywhere
   * (e.g. the empty-state "Reset filters" button) fully clears the bar.
   */
  filtersResetToken?: number;
  drawer?: {
    portCallId?: number;
    appointmentId: number;
    voyageNumber?: string;
  };
};

export const initialDashboardState: BuyerDashboardState = {
  pagination: {
    page: 0,
    pageSize: 5
  },
  filters: {
    myPendingTasks: false,
    mySubmittedAppointments: false,
    vessel: '',
    port: '',
    appointmentId: '',
    regionId: '',
    agent: '',
    portCallId: '',
    voyageNumber: '',
    etaFromDate: '',
    etaToDate: '',
    etsFromDate: '',
    etsToDate: '',
    assignee: '',
    opsCoordinator: ''
  },
  grouping: BuyerDashboardGrouping.VOYAGE,
  filtersResetToken: 0
};

export const buyerDashboardSlice = createSlice({
  name: 'buyer-dashboard',
  initialState: initialDashboardState,
  reducers: {
    updatePage: (state, { payload }: PayloadAction<{ page: number }>) => {
      state.pagination.page = payload.page;
    },
    updateFilter: (
      state,
      { payload }: PayloadAction<Partial<BuyerDashboardState['filters']>>
    ) => {
      state.filters = { ...state.filters, ...payload };
      // reset page
      state.pagination.page = 0;
    },
    updateGrouping: (
      state,
      { payload }: PayloadAction<{ grouping: BuyerDashboardGrouping }>
    ) => {
      state.grouping = payload.grouping;
      // reset page
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = {
        myPendingTasks: false,
        mySubmittedAppointments: false,
        vessel: '',
        port: '',
        appointmentId: '',
        regionId: '',
        agent: '',
        portCallId: '',
        voyageNumber: '',
        etaToDate: '',
        etaFromDate: '',
        etsToDate: '',
        etsFromDate: '',
        assignee: '',
        opsCoordinator: ''
      };
      // reset page
      state.pagination.page = 0;
      state.filtersResetToken = (state.filtersResetToken ?? 0) + 1;
    },
    updateDrawer: (
      state,
      {
        payload
      }: PayloadAction<{
        portCallId?: number;
        appointmentId: number;
        voyageNumber?: string;
      }>
    ) => {
      state.drawer = payload;
    }
  }
});

export const {
  updatePage: updateBuyerDashboardPage,
  updateFilter: updateBuyerDashboardFilter,
  updateGrouping: updateBuyerDashboardGrouping,
  resetFilters: resetBuyerDashboardFilters,
  updateDrawer: updateBuyerDashboardDrawer
} = buyerDashboardSlice.actions;

export type StateWithBuyerDashboard = {
  [buyerDashboardSlice.name]: BuyerDashboardState;
};
